/**
 * UIModelProvider for UI-Model-1 (API-Direct Approach)
 *
 * Composite provider that wraps all UI Model contexts.
 * Provides the complete provider hierarchy for the UI Model editor.
 *
 * Provider Hierarchy:
 * - UIModelApiProvider (API operations)
 * - UIModelStateProvider (core state with useReducer - API format)
 * - SelectionProvider (selection state - needed by NavigationProvider)
 * - NavigationProvider (groups, pages - depends on SelectionProvider)
 * - HeaderProvider (header configuration)
 * - ThemeProvider (theme configuration)
 * - CustomCssProvider (custom CSS)
 * - FieldConfigProvider (field configurations)
 * - SaveQueueProvider (auto-save)
 *
 * Line limit: ~150 lines
 */

import React, { useCallback, useMemo } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { UIModelStateProvider, useUIModelState } from './UIModelStateContext';
import { SelectionProvider } from './SelectionContext';
import { NavigationProvider } from './NavigationContext';
import { HeaderProvider } from './HeaderContext';
import { ThemeProvider } from './ThemeContext';
import { CustomCssProvider } from './CustomCssContext';
import { FieldConfigProvider } from './FieldConfigContext';
import { SaveQueueProvider } from './SaveQueueContext';
import { UIModelApiProvider, useUIModelApi } from './UIModelApiContext';
import { useModule } from './ModuleContext';
import { DirtySection } from '../types/state.types';
import { UIModelProviderProps } from '../types/context.types';
import { SaveDataByType } from '../services/SaveQueue';
import { uiModelApiService } from '../../shared/services/UIModelApiService';
import { updateCustomCSS } from '../../shared/services/ApiHelper';
import CanvasAutoSave from '../components/CanvasAutoSave';
import ModelSpecSyncBridge from '../components/ModelSpecSyncBridge';

// ============================================================================
// Inner Provider (has access to API context)
// ============================================================================

interface SaveQueueWrapperProps {
  children: React.ReactNode;
  configureId?: string;
  debounceMs: number;
  onSaveError?: (error: Error, section: DirtySection) => void;
  onSaveComplete?: (section: DirtySection) => void;
}

const SaveQueueWrapper = ({
  children,
  configureId,
  debounceMs,
  onSaveError,
  onSaveComplete,
}: SaveQueueWrapperProps) => {
  // Get modelId from state context - this is set when a model is loaded
  // This is the critical fix: read modelId from loaded state, not from props
  const { state } = useUIModelState();
  const { hasEditPermission } = useModule();
  const effectiveModelId = state.model?.id;

  // Create the save handler that executes actual API calls
  // Returns undefined (not throwing) when IDs aren't available yet
  const handleSave = useCallback(
    async (section: DirtySection, data: unknown, canvasId?: string): Promise<void> => {
      // Silently skip save if model context isn't ready yet
      // This can happen on initial mount before a model is selected
      if (!configureId || !effectiveModelId) {
        return;
      }

      // Block save if user doesn't have edit permission
      if (!hasEditPermission()) {
        throw new Error('You do not have permission to update this model');
      }

      try {
        switch (section) {
          case 'header': {
            const headerData = data as SaveDataByType['header'];
            await uiModelApiService.updateHeader(
              configureId,
              effectiveModelId,
              headerData.headerData,
              headerData.logoFile,
              headerData.resetButtonIconFile,
              headerData.closeButtonIconFile,
              headerData.saveButtonIconFile
            );
            break;
          }

          case 'footer': {
            const footerData = data as SaveDataByType['footer'];
            await uiModelApiService.updateFooter(
              configureId,
              effectiveModelId,
              footerData.footerData,
              footerData.previousButtonIconFile,
              footerData.nextButtonIconFile,
              footerData.doneButtonIconFile
            );
            break;
          }

          case 'navigation': {
            const navData = data as SaveDataByType['navigation'];
            // Strip nested configuration object - API expects flat structure only
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { configuration, ...cleanNavigation } = navData.navigationData || {};
            // Ensure all navigation properties have values (apply defaults for missing ones)
            const navigationWithDefaults = {
              showNavigation: true,
              orientation: 'Horizontal',
              style: 'Block',
              flow: 'Nonlinear',
              showDivider: true,
              pageSpacing: 10,
              groupSpacing: 10,
              textAlignment: 'Centre',
              ...cleanNavigation,  // State values override defaults
            };
            await uiModelApiService.updateNavigation(configureId, effectiveModelId, { navigation: navigationWithDefaults });
            break;
          }

          case 'canvas': {
            const canvasData = data as SaveDataByType['canvas'];
            const effectiveCanvasId = canvasId || canvasData.canvasId;
            if (!effectiveCanvasId) {
              throw new Error('Canvas ID is required for canvas save');
            }
            await uiModelApiService.updateCanvas(
              configureId,
              effectiveModelId,
              { canvas: { pageId: canvasData.pageId, groupId: canvasData.groupId, containers: canvasData.containers } },
              effectiveCanvasId
            );
            break;
          }

          case 'theme': {
            const themeData = data as SaveDataByType['theme'];
            await uiModelApiService.updateTheme(configureId, effectiveModelId, themeData.themeData);
            break;
          }

          case 'customCss': {
            const cssData = data as SaveDataByType['customCss'];
            await updateCustomCSS(configureId, effectiveModelId, cssData.customCssData);
            break;
          }

          case 'sharedWidgets': {
            const widgetsData = data as SaveDataByType['sharedWidgets'];
            await uiModelApiService.updateSharedWidgets(configureId, effectiveModelId, widgetsData.widgetsData);
            break;
          }

          default:
            console.warn(`Unknown save section: ${section}`);
        }

        onSaveComplete?.(section);
      } catch (error) {
        console.error(`Failed to save ${section}:`, error);
        onSaveError?.(error as Error, section);
        throw error;
      }
    },
    [configureId, effectiveModelId, onSaveComplete, onSaveError]
  );

  const handleError = useCallback(
    (error: Error, section: DirtySection) => {
      console.error(`Save error for ${section}:`, error);
      toast.error(`Failed to save ${section}: ${error.message}`);
      onSaveError?.(error, section);
    },
    [onSaveError]
  );

  return (
    <SaveQueueProvider
      debounceMs={debounceMs}
      onSave={handleSave}
      onSaveError={handleError}
    >
      <CanvasAutoSave />
      <ModelSpecSyncBridge />
      {children}
    </SaveQueueProvider>
  );
};

// ============================================================================
// Main Provider
// ============================================================================

export const UIModelProvider = ({
  children,
  configureId,
  modelId,
  bearerToken,
  debounceMs = 2000,
  onSaveError,
  onSaveComplete,
}: UIModelProviderProps) => {
  return (
    <UIModelApiProvider
      config={{ configureId, modelId, bearerToken }}
    >
      <UIModelStateProvider>
        <SelectionProvider>
          <NavigationProvider>
            <HeaderProvider>
              <ThemeProvider>
                <CustomCssProvider>
                  <FieldConfigProvider>
                    <SaveQueueWrapper
                      configureId={configureId}
                      debounceMs={debounceMs}
                      onSaveError={onSaveError}
                      onSaveComplete={onSaveComplete}
                    >
                      {children}
                    </SaveQueueWrapper>
                  </FieldConfigProvider>
                </CustomCssProvider>
              </ThemeProvider>
            </HeaderProvider>
          </NavigationProvider>
        </SelectionProvider>
      </UIModelStateProvider>
    </UIModelApiProvider>
  );
};

export default UIModelProvider;
